import { useCallback } from 'react';

import { cn } from '@/lib/utils';

import { DMMcpAppView } from '@/components/DMMcpApps/DMMcpAppView';
import { useMcpAppContent } from '@/components/DMMcpApps/useMcpAppContent';
import { Skeleton } from '@/components/ui/skeleton';

interface Props {
    mcpAppMetadata: {
        resourceUri: string;
        serverUrl?: string;
        toolName?: string;
        toolResult: any;
        csp?: any;
        permissions?: any;
    };
    height?: string;
    className?: string;
}

export function McpAppCard({ mcpAppMetadata, height, className }: Props) {
    const { toolName } = mcpAppMetadata;
    const handleToolCall = useCallback(
        async (name: string, args: Record<string, unknown>) => {
            const response = await fetch(
                `${window.location.origin}/mcp/app-tool`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        app_tool_name: toolName,
                        tool_name: name,
                        arguments: args
                    })
                }
            );
            if (!response.ok) {
                return {
                    content: [
                        {
                            type: 'text' as const,
                            text: `Tool call failed (${response.status})`
                        }
                    ],
                    isError: true
                };
            }
            const { result } = await response.json();
            return {
                content: [
                    {
                        type: 'text' as const,
                        text:
                            typeof result === 'string'
                                ? result
                                : JSON.stringify(result)
                    }
                ]
            };
        },
        [toolName]
    );

    const { html, isLoading, isRevalidating, error } = useMcpAppContent({
        resourceUri: mcpAppMetadata.resourceUri,
        serverUrl: mcpAppMetadata.serverUrl,
        enabled: true
    });

    // Only show the full-height spinner on a cold load (no cached value available).
    if (isLoading) {
        return (
            <div
                className={cn(
                    'flex flex-col gap-4 bg-background my-2 p-6 border border-border rounded-lg w-full min-h-[400px]',
                    className
                )}
                aria-busy="true"
                aria-label="Loading chart"
            >
                <Skeleton className="w-1/2 h-5" />
                <Skeleton className="w-1/3 h-3" />
                <div className="flex flex-col flex-1 justify-center gap-3 pt-4">
                    {[100, 78, 56, 40, 28, 18].map((width) => (
                        <Skeleton
                            key={width}
                            className="h-5"
                            style={{ width: `${width}%` }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (error || !html) return null;

    return (
        // Wrap in a relative container so the revalidation overlay doesn't shift layout.
        <div
            className={cn(
                'relative my-2 border border-border rounded-lg w-full overflow-hidden',
                className
            )}
        >
            {isRevalidating && (
                <div
                    className="right-8 bottom-8 z-10 absolute flex items-center bg-background/80 backdrop-blur-xs px-2 py-1 rounded-full text-muted-foreground text-xs"
                    aria-live="polite"
                    aria-label="Refreshing"
                >
                    <div className="border border-border border-t-blue-500 rounded-full w-3 h-3 animate-spin" />
                </div>
            )}
            <>
                <DMMcpAppView
                    html={html}
                    toolArguments={{}}
                    toolResult={{
                        content: [
                            {
                                type: 'text',
                                text: JSON.stringify(mcpAppMetadata.toolResult)
                            }
                        ]
                    }}
                    permissions={mcpAppMetadata.permissions}
                    onToolCall={toolName ? handleToolCall : undefined}
                    height={height}
                />
            </>
        </div>
    );
}
